// Pure geometry helpers for MapDraw measurement labels.
// Extracted from MapDraw.tsx so the coordinate handling can be unit-tested in
// isolation (and guarded against malformed persisted geometry).

// Calculate distance between two coordinates using the Haversine formula (meters)
export const calculateDistance = (
  coord1: [number, number],
  coord2: [number, number]
): number => {
  const [lon1, lat1] = coord1
  const [lon2, lat2] = coord2

  const R = 6371000 // Earth's radius in meters
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return R * c // Distance in meters
}

// Format distance for display
export const formatDistance = (meters: number): string => {
  if (meters < 1000) {
    return `${meters.toFixed(1)}m`
  } else {
    return `${(meters / 1000).toFixed(2)}km`
  }
}

// Calculate midpoint between two coordinates
export const calculateMidpoint = (
  coord1: [number, number],
  coord2: [number, number]
): [number, number] => {
  return [(coord1[0] + coord2[0]) / 2, (coord1[1] + coord2[1]) / 2]
}

// Type guard for a valid [lng, lat] coordinate pair
export const isCoordinatePair = (value: unknown): value is [number, number] =>
  Array.isArray(value) &&
  value.length >= 2 &&
  typeof value[0] === 'number' &&
  typeof value[1] === 'number'

// Build the ordered list of segments (consecutive coordinate pairs) used to
// place per-edge measurement labels.
//
// Malformed/mismatched geometry is tolerated: a drawing may be persisted with a
// `type` that disagrees with its geometry (e.g. type "polygon" but a LineString
// geometry), which would otherwise feed a bare number into calculateDistance and
// crash the whole map. We filter to valid coordinate pairs first, so bad data
// simply yields no labels instead of throwing.
export const getMeasurementSegments = (
  coordinates: unknown,
  isPolygon: boolean
): Array<[[number, number], [number, number]]> => {
  const coords = (Array.isArray(coordinates) ? coordinates : []).filter(
    isCoordinatePair
  )
  if (coords.length < 2) return []

  // For polygons, strip the closing duplicate (GeoJSON rings repeat the first
  // point) before re-closing, so we don't get a 0-distance segment.
  let ring = coords
  if (
    isPolygon &&
    ring[0][0] === ring[ring.length - 1][0] &&
    ring[0][1] === ring[ring.length - 1][1]
  ) {
    ring = ring.slice(0, -1)
  }

  const points = isPolygon ? ring.concat([ring[0]]) : ring

  const segments: Array<[[number, number], [number, number]]> = []
  for (let i = 0; i < points.length - 1; i++) {
    segments.push([points[i], points[i + 1]])
  }
  return segments
}
